import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import debounce from "lodash/debounce";
import {
  toggleContactChangeModal,
  useContactChangeState,
  setCurrentStage,
  STAGES,
  setContactChangeType,
  ContactChangeType,
  setBannerStage,
  BANNER_STAGES,
  setRequestedValue,
} from "src/store/features/contactChange.slice";
import {
  useCreateContactChangeMutation,
  useCheckPasswordMutation,
  useVerifySecondaryAuthCodeMutation,
  useProcessRequestedValuesMutation,
  useVerifyRequestedValueCodeMutation,
  useCheck2FaCodeMutation,
  useInitiateMfaMutation,
  useClearMfaMutation,
  useSubmitContactChangeMutation,
  useCancelContactChangeMutation,
  CreateContactChangeReqDto,
  useGetLatestContactChangeQuery,
  useResendSecondaryAuthCodeMutation,
  useResendRequestedAuthCodeMutation,
} from "src/store/features/api/generated";
import { CountryIso2, defaultCountries, parseCountry } from "react-international-phone";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import { useUserSlice } from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";

export enum MFA_INPUT_STEPS {
  PASSWORD = 1,
  SECONDARY_AUTH,
  NEW_VALUE,
  NEW_VALUE_CODE,
  TWO_FA,
  BUTTON_DISPLAY,
}

const DEFAULT_PHONE_COUNTRY = "us";

export default function useContactChangeForm() {
  const { is2FAEnabled } = useUserSlice();
  const { isGuest } = usePermission();

  const {
    isContactChangeModalOpen,
    contactChangeType,
    currentStage,
    previousStage,
    changingValueName,
    userEmail,
    userPhone,
    secondaryAuthValue,
  } = useContactChangeState();
  const dispatch = useDispatch();

  const confirmButtonRef = useRef<HTMLDivElement | null>(null);

  let title = `Change ${changingValueName}`;
  if (currentStage === STAGES.COMPLETED) {
    title = `Request submitted`;
  } else if (currentStage === STAGES.QUIT) {
    title = `Quit ${contactChangeType} change?`;
  }
  /*************
   * STATE *
   *************/
  // MFA INPUT FORM
  const [currentStep, setCurrentStep] = useState(MFA_INPUT_STEPS.PASSWORD);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  // Secondary auth value is just the opposite of the kind of change you're doing
  // Its the second step of the flow. ie if you're doing an email change, it will send a code to your existing phone number
  // If a phone change, it will send a code to your existing email
  const [secondaryAuthValueCode, setSecondaryAuthValueCode] = useState("");
  const [secondaryAuthValueCodeLoading, setSecondaryAuthValueCodeLoading] = useState(false);
  const [secondaryAuthValueCodeError, setSecondaryAuthValueCodeError] = useState(false);

  // new value is the new contact detail
  // ie if its a email change it will be the email
  // If phone change it will be the phone number
  const [newValue, setNewValue] = useState("");
  const [newValueLoading, setNewValueLoading] = useState(false);
  const [newValueError, setNewValueError] = useState(false);

  const [isSameContactValue, setIsSameContactValue] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("");

  const [newValueCode, setNewValueCode] = useState("");
  const [newValueCodeLoading, setNewValueCodeLoading] = useState(false);
  const [newValueCodeError, setNewValueCodeError] = useState(false);

  const [twoFACode, setTwoFACode] = useState("");
  const [twoFACodeLoading, setTwoFACodeLoading] = useState(false);
  const [twoFACodeError, setTwoFACodeError] = useState(false);

  /*************
   * API CALLS *
   *************/
  const [createContactChange, { isLoading: isLoadingCreate }] = useCreateContactChangeMutation();
  const [checkPassword] = useCheckPasswordMutation();
  const [verifySecondaryAuthCode] = useVerifySecondaryAuthCodeMutation();
  const [resendSecondaryAuthCode, { isLoading: isLoadingResendSecondaryAuthCode }] =
    useResendSecondaryAuthCodeMutation();
  const [processRequestedValues] = useProcessRequestedValuesMutation();
  const [verifyRequestedValueCode] = useVerifyRequestedValueCodeMutation();
  const [resendRequestedAuthCode, { isLoading: isLoadingResendRequestedAuthCode }] =
    useResendRequestedAuthCodeMutation();
  const [check2FaCode] = useCheck2FaCodeMutation();
  const [initiateMFA, { isLoading: isLoadingInitiateMFA }] = useInitiateMfaMutation();
  const [clearMfa, { isLoading: isLoadingClearMFA }] = useClearMfaMutation();
  const [submitContactChange, { isLoading: isLoadingSubmit }] = useSubmitContactChangeMutation();
  const [cancelContactChange, { isLoading: isLoadingCancel }] = useCancelContactChangeMutation();
  const {
    data: latestContactChange,
    isLoading: isLoadingLatest,
    isFetching: isFetchingLatest,
    error: latestContactChangeError,
    refetch: refetchLatestContactChange,
  } = useGetLatestContactChangeQuery(undefined, { skip: isGuest });

  // Utility function for debounced API calls
  const executeDebouncedApiCall = (
    apiCall: any,
    arg: any,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setError: React.Dispatch<React.SetStateAction<boolean>>,
    onSuccess: () => void,
    debounceTime: number = 1000,
  ) => {
    return debounce(async () => {
      setLoading(true);
      setError(false);
      setIsExpired(false);
      try {
        await apiCall(arg).unwrap();
        setLoading(false);
        onSuccess();
      } catch (error: any) {
        setLoading(false);
        setError(true);
        handleAPIError(error);
      }
    }, debounceTime);
  };

  // Use the API call function
  // we cannot use debounce for password input since it looks like its causing issues with users.
  // instead now we get the customer to manually 'submit' their password
  const handleSubmitPassword = () => {
    if (!password) return;

    const debouncedApiCall = executeDebouncedApiCall(
      checkPassword,
      { passwordDto: { password } },
      setPasswordLoading,
      setPasswordError,
      () => {
        setCurrentStep(MFA_INPUT_STEPS.SECONDARY_AUTH);
        setIsPasswordVisible(false);
      },
      0, // set debounce time to 0 since we want to call the API immediately when password is submitted
    );

    debouncedApiCall();
  };

  useEffect(() => {
    if (!secondaryAuthValueCode) return;
    if (secondaryAuthValueCode.length !== 6) return;

    const debouncedApiCall = executeDebouncedApiCall(
      verifySecondaryAuthCode,
      { mfaCodeDto: { code: secondaryAuthValueCode } },
      setSecondaryAuthValueCodeLoading,
      setSecondaryAuthValueCodeError,
      () => setCurrentStep(MFA_INPUT_STEPS.NEW_VALUE),
      0, // set debounce time to 0 since we want to call the API immediately when code is 6 digits
    );

    debouncedApiCall();

    return () => {
      debouncedApiCall.cancel();
    };
  }, [secondaryAuthValueCode]);

  const handleNewValueSubmit = () => {
    if (!newValue) return;
    // if email, check if it contains @ and .
    if (contactChangeType === "email") {
      if (!newValue.includes("@")) return;
      if (!newValue.includes(".")) return;
      // check to ensure . comes after @ to avoid the situation where someone has a . in their name, ie john.doe@gmail.com
      if (newValue.indexOf("@") > newValue.indexOf(".", newValue.indexOf("@") + 1)) return;
    }
    // dont call api if the parser throws an error
    if (contactChangeType === "phone") {
      try {
        parsePhoneNumberWithError(newValue);
      } catch (e) {
        return;
      }
    }

    // if new value is the same as the current value, set the error and return
    if (
      (contactChangeType === "phone" && newValue === userPhone) ||
      (contactChangeType === "email" && newValue === userEmail)
    ) {
      setIsSameContactValue(true);
      setNewValueError(true);
      return;
    }

    const debouncedApiCall = executeDebouncedApiCall(
      processRequestedValues,
      { processRequestedValuesReqDto: { requestedValue: newValue } },
      setNewValueLoading,
      setNewValueError,
      () => {
        setCurrentStep(MFA_INPUT_STEPS.NEW_VALUE_CODE);
        dispatch(setRequestedValue(newValue));
      },
      0, // set debounce time to 0 since we want to call the API immediately when new value is submitted
    );

    debouncedApiCall();
  };

  useEffect(() => {
    if (!newValueCode) return;
    if (newValueCode.length !== 6) return;

    const debouncedApiCall = executeDebouncedApiCall(
      verifyRequestedValueCode,
      { mfaCodeDto: { code: newValueCode } },
      setNewValueCodeLoading,
      setNewValueCodeError,
      () => {
        // is phone change and 2fa is disabled
        if (contactChangeType === "phone" && !is2FAEnabled) {
          setCurrentStep(MFA_INPUT_STEPS.BUTTON_DISPLAY);
        } else {
          setCurrentStep(MFA_INPUT_STEPS.TWO_FA);
        }
      },
      0, // set debounce time to 0 since we want to call the API immediately when code is 6 digits
    );

    debouncedApiCall();

    return () => {
      debouncedApiCall.cancel();
    };
  }, [newValueCode]);

  useEffect(() => {
    if (!twoFACode) return;
    if (twoFACode.length !== 6) return;

    const debouncedApiCall = executeDebouncedApiCall(
      check2FaCode,
      { mfaCodeDto: { code: twoFACode } },
      setTwoFACodeLoading,
      setTwoFACodeError,
      () => setCurrentStep(MFA_INPUT_STEPS.BUTTON_DISPLAY),
      0, // set debounce time to 0 since we want to call the API immediately when code is 6 digits
    );

    debouncedApiCall();

    return () => {
      debouncedApiCall.cancel();
    };
  }, [twoFACode]);

  /*************
   * INPUT REF HANDLING *
   *************/
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const secondaryAuthValueCodeInputRef = useRef<HTMLInputElement | null>(null);
  const newValueInputRef = useRef<HTMLInputElement | null>(null);
  const newValueCodeInputRef = useRef<HTMLInputElement | null>(null);
  const twoFACodeInputRef = useRef<HTMLInputElement | null>(null);

  const [selection, setSelection] = useState<any>();
  const [selectedInput, setSelectedInput] = useState<React.RefObject<HTMLInputElement> | null>(
    null,
  );

  useEffect(() => {
    if (selection) {
      const { start, end } = selection;
      selectedInput?.current?.focus();
      selectedInput?.current?.setSelectionRange(start, end);
    }
  }, [selection]);

  const setInputSelection = (
    inputRef: React.RefObject<HTMLInputElement>,
    selection: { start: number | null; end: number | null },
  ) => {
    if (inputRef.current && selection.start !== null && selection.end !== null) {
      setSelection({ start: selection.start, end: selection.end });
      setSelectedInput(inputRef);
    }
  };

  /*************
   * HANDLERS *
   *************/
  // INPUT FIELDS
  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.target;
    setPassword(value);
    setInputSelection(passwordInputRef, { start: selectionStart, end: selectionEnd });
  };

  const handleSecondaryAuthValueCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.target;
    const inputCode = value.replace(/[^0-9]/g, "");
    if (inputCode?.length <= 6) {
      setSecondaryAuthValueCode(inputCode);
      setInputSelection(secondaryAuthValueCodeInputRef, {
        start: selectionStart,
        end: selectionEnd,
      });
    }
  };

  const handleNewValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.target;

    if (contactChangeType === "phone") {
      // dont allow + to be deleted
      if (newValue === "+" && !value) return;

      // only allow numbers for a phone
      let phone = value.replace(/[^0-9]/g, "");
      phone = "+" + phone; // add + back to the phone since it would be removed by the regex

      // special edge case for Australia, if a customer enters 04 after the +61, it should be +614
      if (phone.length > 6) {
        if (phone.startsWith("+61") && phone[3] === "0") {
          phone = "+61" + phone.slice(4);
        }
      }
      setNewValue(phone);

      // parse the phone number to get the country and change the flag in the input field
      findCountryFromPhoneNumber(phone);
    } else {
      setNewValue(value.toLowerCase()); // make lowercase since all other systems need email in lowercase
    }

    setInputSelection(newValueInputRef, { start: selectionStart, end: selectionEnd });
  };

  const handleNewValueCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.target;
    const inputCode = value.replace(/[^0-9]/g, "");
    if (inputCode?.length <= 6) {
      setNewValueCode(inputCode);
      setInputSelection(newValueCodeInputRef, { start: selectionStart, end: selectionEnd });
    }
  };

  const handleTwoFACodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.target;
    const inputCode = value.replace(/[^0-9]/g, "");
    if (inputCode?.length <= 6) {
      setTwoFACode(inputCode);
      setInputSelection(twoFACodeInputRef, { start: selectionStart, end: selectionEnd });
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  // PHONE NUMBER INPUT LOGIC
  useEffect(() => {
    if (contactChangeType === "phone" && phoneCountry) {
      const foundCountry = defaultCountries.find((c) => {
        const parsedCountry = parseCountry(c);
        return parsedCountry.iso2 === phoneCountry;
      });
      const dialCode = foundCountry ? parseCountry(foundCountry)?.dialCode : null;
      if (dialCode && dialCode !== phoneCountryCode) {
        setPhoneCountryCode(dialCode);
        setNewValue(`+${dialCode}`);
      }
    }
  }, [phoneCountry]);

  useEffect(() => {
    // when first getting to the new value step, set the phone country to the default.
    // there was a weird issue where the state wouldn't carry over, so now we need to
    // explicitly do it when the user first gets to the new value step.
    if (currentStep === MFA_INPUT_STEPS.NEW_VALUE && contactChangeType === "phone") {
      handlePhoneCountryChange(DEFAULT_PHONE_COUNTRY);
    }
  }, [currentStep]);

  const handlePhoneCountryChange = (country: CountryIso2) => {
    setPhoneCountry(country);
  };

  const findCountryFromPhoneNumber = (phone: string) => {
    try {
      // this will throw an error if the phone number is invalid or incomplete.
      // once the full number or the majority of the number is entered, the country will be set.
      const parsedPhone = parsePhoneNumberWithError(phone);
      if (parsedPhone && parsedPhone.country) {
        setPhoneCountry(parsedPhone.country.toLowerCase());
      } else {
        // if no country has been found, throw error so the best approximate can be used
        throw new Error("No country found");
      }
    } catch (e) {
      // if the phone number is invalid or incomplete, use the default countries and find the closest match.
      // ie. if the number starts with +1, there are a few countries that start with +1, so we just use the first one until the user enters more digits.
      const country = defaultCountries.find((c) => {
        const parsedCountry = parseCountry(c);
        return phone.includes(`+${parsedCountry.dialCode}`);
      });
      if (country) {
        // since country could be any number of countries, we need to check if the dial code is different from the current phone country code.
        const parsedCountry = parseCountry(country);
        if (parsedCountry.dialCode !== phoneCountryCode) {
          setPhoneCountry(parsedCountry.iso2);
        }
      }
    }
  };

  // ERROR HANDLING & BUTTON CLICK HANDLERS
  const handleExpiredContactChange = async () => {
    setIsExpired(true);
    resetInputForm();
    await initiateMFA();
  };

  const handleCannotProcessContactChange = () => {
    dispatch(setCurrentStage(STAGES.CANNOT_PROCESS));
  };

  const handleAPIError = (error: any) => {
    if (error?.message?.includes("contact change request has expired")) {
      handleExpiredContactChange();
    }
    if (error?.message?.includes("contact change request cannot be processed")) {
      handleCannotProcessContactChange();
    }
  };

  const goToNextStage = async () => {
    switch (currentStage) {
      case STAGES.ACKNOWLEDGE:
        dispatch(setCurrentStage(STAGES.FORM));
        break;
      case STAGES.FORM:
        try {
          const resp = await submitContactChange().unwrap();
          if (resp.status === "PENDING_ONFIDO") {
            dispatch(setCurrentStage(STAGES.ONFIDO));
          } else {
            dispatch(setCurrentStage(STAGES.COMPLETED));
          }
        } catch (error: any) {
          console.error("Failed to submit contact change:", error);
          setSubmitErrorMessage("Something went wrong, please try again.");
          handleAPIError(error);
        }
        break;
      case STAGES.ONFIDO:
        dispatch(setCurrentStage(STAGES.COMPLETED));
        break;
      default:
        break;
    }
  };

  const handleNextButtonClick = () => {
    goToNextStage();
  };

  const handleClose = () => {
    // if any of the API calls are loading, do not close the modal
    if (isLoadingCreate || isLoadingSubmit || isLoadingCancel || isLoadingClearMFA) return;

    if (currentStage === STAGES.QUIT) {
      dispatch(setCurrentStage(previousStage)); // Revert to previous stage
    } else if (
      currentStage === STAGES.COMPLETED ||
      currentStage === STAGES.ACKNOWLEDGE ||
      currentStage === STAGES.CANNOT_PROCESS
    ) {
      dispatch(toggleContactChangeModal()); // close modal
      refetchLatestContactChange();
    } else {
      dispatch(setCurrentStage(STAGES.QUIT));
    }
  };

  const handleCreateContactChange = async () => {
    if (!contactChangeType) {
      console.error("Contact change type is not set.");
      return;
    }
    try {
      const contactChangeData: CreateContactChangeReqDto = {
        type: contactChangeType.toUpperCase() as "EMAIL" | "PHONE",
      };
      await createContactChange({
        createContactChangeReqDto: contactChangeData,
      }).unwrap();
      goToNextStage();
    } catch (error) {
      console.error("Failed to create contact change:", error);
      handleAPIError(error);
    }
  };

  const handleAcknowledgeClick = () => {
    handleCreateContactChange();
  };

  const resetInputForm = () => {
    setCurrentStep(MFA_INPUT_STEPS.PASSWORD);
    setIsPasswordVisible(false);
    // clear all input fields
    setPassword("");
    setSecondaryAuthValueCode("");
    setNewValue("");
    setPhoneCountryCode("");
    setPhoneCountry("");
    setNewValueCode("");
    setTwoFACode("");
    // clear all errors
    setPasswordError(false);
    setSecondaryAuthValueCodeError(false);
    setNewValueError(false);
    setNewValueCodeError(false);
    setTwoFACodeError(false);
    // clear all loading
    setPasswordLoading(false);
    setSecondaryAuthValueCodeLoading(false);
    setNewValueLoading(false);
    setNewValueCodeLoading(false);
    setTwoFACodeLoading(false);
  };

  const handleQuitFormButton = async () => {
    if (previousStage === STAGES.FORM) {
      try {
        await clearMfa().unwrap();
      } catch (error) {
        console.error("Failed to clear MFA:", error);
      }
    }
    // close modal whether or not the clearMfa call was successful
    resetInputForm();
    dispatch(toggleContactChangeModal()); // close modal
    await refetchLatestContactChange();
    dispatch(setCurrentStage(previousStage));
  };

  const handleCancelContactChange = async () => {
    try {
      await cancelContactChange().unwrap();
      refetchLatestContactChange();
      dispatch(setBannerStage(null));
      console.log("Contact change cancelled");
    } catch (error) {
      console.error("Failed to cancel contact change:", error);
    }
  };

  const handleContinueButtonClick = async () => {
    if (isContactChangeModalOpen) return; // very weird case where the modal is open and this function is called
    resetInputForm(); // reset the input form to clear any errors or loading
    try {
      // only initiate MFA if the user is in the form stage, otherwise if Onfido stage, MFA is already done
      if (currentStage === STAGES.FORM) {
        setIsExpired(false);
        await initiateMFA().unwrap();
      }
      dispatch(toggleContactChangeModal()); // open modal
    } catch (error) {
      console.error("Failed to initiate MFA:", error);
    }
  };

  const handleResendSecondaryAuthCode = async () => {
    try {
      await resendSecondaryAuthCode().unwrap();
      setSecondaryAuthValueCode("");
      setSecondaryAuthValueCodeError(false);
      setSecondaryAuthValueCodeLoading(false);
    } catch (error) {
      console.error("Failed to resend secondary auth code:", error);
    }
  };

  const handleResendRequestedAuthCode = async () => {
    try {
      await resendRequestedAuthCode().unwrap();
      setNewValueCode("");
      setNewValueCodeError(false);
      setNewValueCodeLoading(false);
    } catch (error) {
      console.error("Failed to resend requested auth code:", error);
    }
  };

  /*************
   * USE EFFECTS *
   *************/
  useEffect(() => {
    if (currentStep === MFA_INPUT_STEPS.BUTTON_DISPLAY && confirmButtonRef.current) {
      confirmButtonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentStep]);

  useEffect(() => {
    if (latestContactChange) {
      const { status, type, requestedValue } = latestContactChange;
      if (type) {
        dispatch(setContactChangeType(type.toLowerCase() as ContactChangeType));
      }

      // reset all stages
      resetInputForm();
      dispatch(setBannerStage(null));
      dispatch(setCurrentStage(STAGES.ACKNOWLEDGE));
      // set new value to the requested value if it exists
      dispatch(setRequestedValue(requestedValue ?? ""));

      if (["PENDING_MFA", "PENDING_ONFIDO"].includes(status)) {
        dispatch(setBannerStage(BANNER_STAGES.ACTION_REQUIRED));
      } else if (status === "FROZEN") {
        dispatch(setBannerStage(BANNER_STAGES.COMPLETED));
      } else if (["PROCESSING", "MANUAL_REVIEW", "ONFIDO_SUBMITTED"].includes(status)) {
        dispatch(setBannerStage(BANNER_STAGES.IN_PROGRESS));
      }

      if (status === "PENDING_MFA") {
        dispatch(setCurrentStage(STAGES.FORM));
      } else if (status === "PENDING_ONFIDO") {
        dispatch(setCurrentStage(STAGES.ONFIDO));
      }
    }
  }, [latestContactChange]);

  return {
    handleClose,
    title,
    handleNextButtonClick,
    handleAcknowledgeClick,
    currentStep,
    passwordError,
    password,
    handlePasswordChange,
    passwordLoading,
    isPasswordVisible,
    togglePasswordVisibility,
    secondaryAuthValue, // In case of email change this will be the phone number, otherwise it will be the email address
    secondaryAuthValueCodeError,
    secondaryAuthValueCode,
    handleSecondaryAuthValueCodeChange,
    secondaryAuthValueCodeLoading,
    changingValueName,
    newValueError,
    newValue,
    handleNewValueChange,
    newValueLoading,
    newValueCodeError,
    newValueCode,
    handleNewValueCodeChange,
    newValueCodeLoading,
    twoFACodeError,
    twoFACode,
    handleTwoFACodeChange,
    twoFACodeLoading,
    confirmButtonRef,
    userEmail,
    userPhone,
    handleCreateContactChange,
    latestContactChange,
    latestContactChangeError,
    handleQuitFormButton,
    handleCancelContactChange,
    handleContinueButtonClick,
    acknowledgeLoading: isLoadingCreate,
    submitLoading: isLoadingSubmit,
    cancelLoading: isLoadingCancel,
    initiateLoading: isLoadingInitiateMFA,
    clearMfaLoading: isLoadingClearMFA,
    latestContactChangeLoading: isLoadingLatest || isFetchingLatest,
    submitErrorMessage,
    handleResendSecondaryAuthCode,
    handleResendRequestedAuthCode,
    resendSecondaryAuthCodeLoading: isLoadingResendSecondaryAuthCode,
    resendRequestedAuthCodeLoading: isLoadingResendRequestedAuthCode,
    isSameContactValue,
    passwordInputRef,
    secondaryAuthValueCodeInputRef,
    newValueInputRef,
    newValueCodeInputRef,
    twoFACodeInputRef,
    isExpired,
    phoneCountry,
    handlePhoneCountryChange,
    handleSubmitPassword,
    handleNewValueSubmit,
  };
}
